/**
 * Device fingerprinting — generates a stable device identifier for session tracking.
 *
 * This is NOT a tracking fingerprint. It's used for:
 * - Single-device session enforcement (identifying which device is logged in)
 * - MFA bypass on known/trusted devices
 *
 * The fingerprint is a hash of browser characteristics that remain stable across sessions.
 */

async function hashString(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function getDeviceFingerprint(): Promise<string> {
  // Read the browser TZ via the time module so the fingerprint formula
  // and the display layer use the same source. The lint guard against
  // direct Intl.DateTimeFormat use is the reason this is centralized.
  const { BROWSER_TZ } = await import("@/lib/time");
  const components = [
    navigator.userAgent,
    navigator.language,
    `${screen.width}x${screen.height}`,
    `${screen.colorDepth}`,
    BROWSER_TZ,
    navigator.hardwareConcurrency?.toString() ?? "unknown",
  ];

  return hashString(components.join("|"));
}

export function getDeviceName(): string {
  const ua = navigator.userAgent;

  // Detect OS
  let os = "Unknown OS";
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  // Detect browser
  let browser = "Unknown Browser";
  if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Safari")) browser = "Safari";

  return `${browser} on ${os}`;
}
